import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/themes";
import { toast } from "sonner";

export function WalletTab({ onData }) {
  const { isParent, refreshMembers } = useAuth();
  const [data, setData] = useState({ fund: { balance: 0 }, members: [], transactions: [] });
  const [loadAmt, setLoadAmt] = useState("");
  const [payoutFor, setPayoutFor] = useState(null);
  const [payoutAmt, setPayoutAmt] = useState("");

  const load = async () => setData((await api.get("/wallet")).data);
  useEffect(() => { load(); }, []);

  const topUp = async () => {
    const amt = parseFloat(loadAmt);
    if (!amt || amt <= 0) return toast.error("Enter an amount");
    await api.post("/wallet/load", { amount: amt });
    setLoadAmt("");
    await load(); onData?.();
    toast.success(`Added ${fmtMoney(amt)} to the chore fund 💰`);
  };

  const doPayout = async (memberId) => {
    const amt = parseFloat(payoutAmt);
    if (!amt || amt <= 0) return toast.error("Enter an amount");
    try {
      await api.post("/wallet/payout", { member_id: memberId, amount: amt });
      setPayoutFor(null); setPayoutAmt("");
      await load(); await refreshMembers(); onData?.();
      toast.success("Paid out! 🎉");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payout failed");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-extrabold" style={{ fontFamily: "Outfit" }}>Chore Fund & Wallets</h2>
        <p className="text-slate-500 font-medium">Parents load the fund; kids earn as chores get approved.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        <div className="card-tactile p-6 md:col-span-1" style={{ background: "linear-gradient(160deg,#FFF7E6,#FEF3C7)" }}>
          <div className="text-sm font-bold uppercase tracking-wide text-slate-500">Family Chore Fund</div>
          <div data-testid="fund-balance" className="text-4xl font-extrabold mt-1" style={{ fontFamily: "Outfit" }}>
            {fmtMoney(data.fund?.balance)}
          </div>
          {isParent ? (
            <div className="flex gap-2 mt-4">
              <input data-testid="load-fund-input" type="number" placeholder="0.00" value={loadAmt}
                onChange={(e) => setLoadAmt(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border-2 border-slate-200 outline-none font-bold" />
              <button data-testid="load-fund-btn" onClick={topUp} className="btn-tactile bg-[#10B981] text-white px-4 shrink-0">Load</button>
            </div>
          ) : (
            <p className="text-xs text-slate-400 mt-3 font-medium">Only parents can top up the fund.</p>
          )}
        </div>

        <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
          {data.members.map((m) => (
            <div key={m.id} className="card-tactile p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-9 h-9 rounded-full flex items-center justify-center text-lg" style={{ background: m.color + "33" }}>{m.avatar || "🙂"}</span>
                <div className="font-extrabold" style={{ color: m.color }}>{m.name}</div>
              </div>
              <div data-testid={`balance-${m.name.toLowerCase()}`} className="text-2xl font-extrabold">{fmtMoney(m.balance)}</div>
              <div className="text-xs font-bold text-[#8B5CF6] mt-0.5">🍫 {m.brownie_points} brownie pts</div>
              {isParent && m.role === "kid" && (
                payoutFor === m.id ? (
                  <div className="flex gap-2 mt-3">
                    <input type="number" placeholder="0.00" value={payoutAmt} autoFocus
                      onChange={(e) => setPayoutAmt(e.target.value)}
                      className="w-full px-3 py-1.5 rounded-lg border-2 border-slate-200 outline-none font-bold text-sm" />
                    <button onClick={() => doPayout(m.id)} className="btn-tactile bg-[#FF7A00] text-white px-3 text-sm shrink-0">Pay</button>
                  </div>
                ) : (
                  <button data-testid={`payout-btn-${m.name.toLowerCase()}`} onClick={() => setPayoutFor(m.id)}
                    className="btn-tactile bg-white text-slate-700 text-xs px-3 py-1.5 mt-3">Cash out 💸</button>
                )
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card-tactile p-5">
        <h3 className="font-bold mb-3" style={{ fontFamily: "Outfit" }}>Recent activity</h3>
        <div className="space-y-1.5 max-h-72 overflow-auto">
          {data.transactions.length === 0 && <p className="text-slate-400 font-medium text-sm">No activity yet.</p>}
          {data.transactions.map((t) => (
            <div key={t.id} className="flex items-center gap-3 text-sm py-1.5 border-b border-slate-100 last:border-0">
              <span className="text-lg">{t.type === "load" ? "💰" : t.type === "payout" ? "💸" : "✅"}</span>
              <span className="flex-1 font-medium">{t.note}</span>
              <span className={`font-bold ${t.type === "payout" ? "text-red-500" : "text-emerald-600"}`}>
                {t.type === "payout" ? "-" : "+"}{fmtMoney(t.amount)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <p className="text-xs text-slate-400 font-medium text-center">
        💡 This is an in-app chore fund ledger. Connecting a real bank / Revolut payout can be added later.
      </p>
    </div>
  );
}
